/**
 * Game over (docs/GAME_DESIGN.md § 8): both scores, the run's stats, the
 * checkpoint sparkline, where the run landed on the leaderboards (or a
 * friendly invitation to join them), a personal-best burst, and Play again.
 * The view model is screens/model/gameOver.ts.
 */
import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { DEFAULT_GAME_CONFIG, type Locale } from '@dropstack/core';
import type { AdsService, PlayerProfile } from '../contracts.ts';
import { useT } from '../i18n/index.ts';
import { AdSlot } from '../components/AdSlot.tsx';
import { Button } from '../components/Button.tsx';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Sparkline } from '../components/Sparkline.tsx';
import { Spinner } from '../components/Spinner.tsx';
import { formatInteger } from './model/format.ts';
import {
  gameOverStats,
  headlineKey,
  isPersonalBest,
  shownScores,
  submitView,
  type FinishedRun,
  type SubmitView,
} from './model/gameOver.ts';
import { SCOPE_LABEL_KEYS, type RankChip } from './model/leaderboard.ts';
import { levelSeries, ratingSeries } from './model/sparkline.ts';
import './gameover.css';

export interface GameOverScreenProps {
  run: FinishedRun;
  profile: PlayerProfile | null;
  /** The player said "not now" to joining the leaderboard before. */
  joinDismissed: boolean;
  locale: Locale;
  starting: boolean;
  ads: AdsService;
  adsEnabled: boolean;
  onPlayAgain(): void;
  onLeaderboards(): void;
  onHome(): void;
  onJoin(): void;
  onDismissJoin(): void;
  onRetrySubmit(): void;
}

export function GameOverScreen(props: GameOverScreenProps) {
  const { run, profile, joinDismissed, locale, starting, ads, adsEnabled } = props;
  const t = useT();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const { summary } = run;
  const scores = shownScores(run);
  const best = isPersonalBest(run);
  const view = submitView({ run, profile, joinDismissed, now: Date.now(), locale });
  const levels = levelSeries(summary);

  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
  }, []);

  return (
    <main className="gameover" aria-labelledby="gameover-title">
      <header className="gameover__head">
        {best && <PersonalBestBurst />}
        <h1 id="gameover-title" ref={headingRef} tabIndex={-1} className="gameover__title">
          {t(headlineKey(summary, run.quit))}
        </h1>
        {best && (
          <p className="gameover__best">
            <Icon name="star" />
            {t('shell.goPersonalBest')}
          </p>
        )}
      </header>

      <section className="gameover__scores" aria-label={t('shell.goScoresLabel')}>
        <div className="ui-panel gameover__score gameover__score--raw">
          <span className="gameover__score-label">{t('shell.goRaw')}</span>
          <span className="gameover__score-value">{formatInteger(scores.raw)}</span>
          <span className="gameover__score-note">{t('shell.goRawNote')}</span>
        </div>
        <div className="ui-panel gameover__score gameover__score--rated">
          <span className="gameover__score-label">{t('shell.goRated')}</span>
          <span className="gameover__score-value">{formatInteger(scores.rated)}</span>
          <span className="gameover__score-note">{t('shell.goRatedNote')}</span>
        </div>
      </section>

      <section className="ui-panel gameover__stats" aria-label={t('shell.goStatsLabel')}>
        <dl className="gameover__grid">
          {gameOverStats(summary, locale).map((stat) => (
            <div key={stat.id} className="gameover__stat">
              <dt>{t(stat.labelKey)}</dt>
              <dd>
                <span className="gameover__stat-value">{stat.value}</span>
                {stat.detail && <span className="gameover__stat-detail">{t(stat.detail.key, stat.detail.params)}</span>}
              </dd>
            </div>
          ))}
        </dl>
        <div className="gameover__spark">
          <h2 className="gameover__spark-title">{t('shell.goJourney')}</h2>
          {summary.checkpoints.length > 0 ? (
            <Sparkline
              values={ratingSeries(summary)}
              label={t('shell.goJourneyLabel', { levels: levels.join(' → ') })}
            />
          ) : (
            <p className="gameover__spark-empty">{t('shell.goJourneyEmpty', { n: DEFAULT_GAME_CONFIG.checkpointSize })}</p>
          )}
        </div>
      </section>

      <section className="ui-panel gameover__board" aria-live="polite">
        <SubmitStatus view={view} t={t} {...props} />
      </section>

      <div className="gameover__actions">
        <Button variant="play" size="lg" icon="play" busy={starting} onClick={props.onPlayAgain}>
          {t('shell.goPlayAgain')}
        </Button>
        <div className="gameover__secondary">
          <Button icon="trophy" disabled={starting} onClick={props.onLeaderboards}>
            {t('shell.navLeaderboards')}
          </Button>
          <Button icon="home" disabled={starting} onClick={props.onHome}>
            {t('shell.navHome')}
          </Button>
        </div>
      </div>

      <div className="gameover__ad">
        <AdSlot ads={ads} slot="gameover-banner" enabled={adsEnabled} />
      </div>
    </main>
  );
}

type Translate = ReturnType<typeof useT>;

function SubmitStatus({ view, t, onJoin, onDismissJoin, onRetrySubmit }: GameOverScreenProps & { view: SubmitView; t: Translate }) {
  switch (view.kind) {
    case 'no-answers':
      return <StatusLine icon="info">{t('shell.goNoAnswers')}</StatusLine>;
    case 'saving':
      return <Spinner size="small" label={t('shell.goSaving')} />;
    case 'ranked':
      return view.chips.length > 0 ? (
        <>
          <h2 className="gameover__board-title">{t('shell.goRanksTitle')}</h2>
          <ul className="gameover__ranks">
            {view.chips.map((chip) => (
              <li key={chip.scope} className="ui-rank-chip">
                <span className="ui-rank-chip__place">{chipLabel(chip, t)}</span>
                <span className="ui-rank-chip__rank">#{formatInteger(chip.rank)}</span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <StatusLine icon="check">{t('shell.goSaved')}</StatusLine>
      );
    case 'join':
      return view.quiet ? (
        <div className="gameover__join gameover__join--quiet">
          <p>{t('shell.goJoinQuiet')}</p>
          <Button variant="ghost" icon="trophy" onClick={onJoin}>
            {t('shell.goJoinButton')}
          </Button>
        </div>
      ) : (
        <div className="gameover__join">
          <h2 className="gameover__board-title">{t('shell.goJoinTitle')}</h2>
          <p>{view.onboarded ? t('shell.goJoinOptIn') : t('shell.goJoinBody')}</p>
          <div className="gameover__join-actions">
            <Button variant="primary" icon="trophy" onClick={onJoin}>
              {t('shell.goJoinButton')}
            </Button>
            <Button variant="ghost" onClick={onDismissJoin}>
              {t('shell.goJoinLater')}
            </Button>
          </div>
        </div>
      );
    case 'placement':
      return (
        <div className="gameover__placement">
          <h2 className="gameover__board-title">{t('shell.goPlacementTitle')}</h2>
          <StatusLine icon="check">{t('shell.goPlacement')}</StatusLine>
          {view.invite && (
            <div className="gameover__join gameover__join--quiet">
              <p>{t('shell.goPlacementJoin')}</p>
              <Button variant="ghost" icon="trophy" onClick={onJoin}>
                {t('shell.goJoinButton')}
              </Button>
            </div>
          )}
        </div>
      );
    case 'relaxed':
      return (
        <div className="gameover__relaxed">
          <h2 className="gameover__board-title">{t('shell.goRelaxedTitle')}</h2>
          <StatusLine icon="check">{t('shell.goRelaxed')}</StatusLine>
        </div>
      );
    case 'review':
      return <StatusLine icon="eye">{t('shell.goReview')}</StatusLine>;
    case 'offline':
      return (
        <RetryLine icon="wifi-off" text={t('shell.goOffline')} canRetry={view.canRetry} onRetry={onRetrySubmit} t={t} />
      );
    case 'refused':
      return <StatusLine icon="info">{t(view.messageKey)}</StatusLine>;
    case 'error':
      return <RetryLine icon="info" text={t('shell.goSubmitError')} canRetry={view.canRetry} onRetry={onRetrySubmit} t={t} />;
  }
}

function chipLabel(chip: RankChip, t: Translate): string {
  if (chip.scope === 'grade' && chip.grade !== null) return t('common.gradeN', { grade: chip.grade });
  return chip.place ?? t(SCOPE_LABEL_KEYS[chip.scope]);
}

function StatusLine({ icon, children }: { icon: IconName; children: ReactNode }) {
  return (
    <p className="gameover__status">
      <Icon name={icon} />
      <span>{children}</span>
    </p>
  );
}

function RetryLine(props: { icon: IconName; text: string; canRetry: boolean; onRetry(): void; t: Translate }) {
  return (
    <div className="gameover__retry">
      <StatusLine icon={props.icon}>{props.text}</StatusLine>
      {props.canRetry && (
        <Button icon="refresh" onClick={props.onRetry}>
          {props.t('common.tryAgain')}
        </Button>
      )}
    </div>
  );
}

/** A soft star burst behind the headline (static under reduced motion). */
function PersonalBestBurst() {
  return (
    <span className="gameover__burst" aria-hidden="true">
      {Array.from({ length: 10 }, (_, i) => (
        <span key={i} className="gameover__ray" style={{ '--r': i } as CSSProperties} />
      ))}
    </span>
  );
}
